// Grilla del builder admin (edit-mode). Reusa el mismo grid .vz-grid del modo
// lectura, pero cada widget se envuelve en VisualCard (controles) y hay un
// toolbar para agregar visuales y materializar el data_sql. Estado en useVisualDashboard.
import React, { useState } from 'react';
import { useVisualDashboard } from '../hooks/useVisualDashboard';
import {
  runVisualSql,
  refreshVisualWidget,
  refreshVisualDashboard,
} from '../services/visualsBuilderService';
import VisualCard from '../pages/VisualsPage/widgets/VisualCard';
import VisualWidgetEditorModal from '../pages/VisualsPage/widgets/VisualWidgetEditorModal';

export default function VisualBuilder({ username, data }) {
  const vd = useVisualDashboard(username);
  const [modal, setModal] = useState({ open: false, mode: 'create', initial: null });
  const [busy, setBusy] = useState(false);
  const [materializing, setMaterializing] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const theme = vd.dashboard?.theme || {};
  const ordered = [...vd.widgets].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const closeModal = () => setModal({ open: false, mode: 'create', initial: null });

  const submit = async (payload) => {
    setBusy(true);
    try {
      if (modal.mode === 'edit') await vd.editWidget(modal.initial.id, payload);
      else await vd.addWidget(payload);
      closeModal();
    } catch (e) {
      // vd ya setea vd.error; dejamos el modal abierto para reintentar.
    } finally {
      setBusy(false);
    }
  };

  // "Probar SQL" del modal: ejecuta sin guardar y devuelve {rows, ...}.
  const runSql = (sql) => {
    if (!vd.dashboard) return Promise.reject(new Error('Dashboard no cargado'));
    return runVisualSql(vd.dashboard.id, { username, data_sql: sql });
  };

  // Materializa 1 widget (ejecuta su data_sql guardado) y recarga.
  const materializeWidget = async (widgetId) => {
    if (!vd.dashboard) return;
    setActionMsg(null);
    try {
      await refreshVisualWidget(vd.dashboard.id, widgetId, username);
      await vd.load();
      setActionMsg({ ok: true, text: 'Widget materializado.' });
    } catch (e) {
      setActionMsg({ ok: false, text: e.message || 'Error al materializar el widget.' });
    }
  };

  // Materializa todos los widgets con data_sql.
  const materializeAll = async () => {
    if (!vd.dashboard || materializing) return;
    setMaterializing(true);
    setActionMsg(null);
    try {
      const res = await refreshVisualDashboard(vd.dashboard.id, username);
      await vd.load();
      const counts = Object.values(res?.results || {});
      const ready = counts.filter((s) => s === 'ready').length;
      const errors = counts.filter((s) => s === 'error').length;
      setActionMsg({ ok: errors === 0, text: `Materializados ${ready}${errors ? ` · ${errors} con error` : ''}.` });
    } catch (e) {
      setActionMsg({ ok: false, text: e.message || 'Error al materializar el tablero.' });
    } finally {
      setMaterializing(false);
    }
  };

  return (
    <>
      <div className="vz-edit-toolbar">
        <button className="vz-btn-primary" onClick={() => setModal({ open: true, mode: 'create', initial: null })}>
          + Agregar visual
        </button>
        <button className="vz-btn-secondary" onClick={materializeAll} disabled={materializing}>
          {materializing ? 'Materializando…' : '↻ Materializar todo'}
        </button>
        <span className="vz-edit-hint">Modo edición — edita el código (render + SQL) de cada visual</span>
        {actionMsg && <span className={actionMsg.ok ? 'vz-edit-hint' : 'vz-error-inline'}>{actionMsg.text}</span>}
        {vd.error && <span className="vz-error-inline">{vd.error}</span>}
      </div>

      {vd.status === 'loading' && <div className="vz-loading">Cargando estructura…</div>}

      <div className="vz-grid">
        {ordered.map((w, i) => (
          <VisualCard
            key={w.id} widget={w} theme={theme} data={data}
            canUp={i > 0} canDown={i < ordered.length - 1}
            onEdit={() => setModal({ open: true, mode: 'edit', initial: w })}
            onDelete={() => { if (window.confirm(`¿Eliminar "${w.title || 'sin título'}"?`)) vd.removeWidget(w.id); }}
            onMoveUp={() => vd.moveWidget(i, -1)}
            onMoveDown={() => vd.moveWidget(i, +1)}
            onMaterialize={() => materializeWidget(w.id)}
          />
        ))}
      </div>

      {ordered.length === 0 && vd.isReady && (
        <div className="vz-empty">Este tablero no tiene visuales. Usa “+ Agregar visual”.</div>
      )}

      <VisualWidgetEditorModal
        open={modal.open} mode={modal.mode} initial={modal.initial}
        theme={theme} data={data} busy={busy}
        onSubmit={submit} onRunSql={runSql} onClose={() => !busy && closeModal()}
      />
    </>
  );
}
